import React, { useContext, useEffect, useState, useCallback } from 'react';
import { AuthContext } from '../../../components/AuthContext';
import api from '../../../api';
import { FaUndo, FaSync, FaFilter } from 'react-icons/fa';

const inr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const GoodsReturnReports = () => {
  const { user } = useContext(AuthContext);
  const companyId = user?.user?.company?._id || user?.user?.company;

  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  useEffect(() => {
    const i = setInterval(() => setDarkMode(localStorage.getItem('darkMode') === 'true'), 500);
    return () => clearInterval(i);
  }, []);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ dateFrom: '', dateTo: '' });

  // Detailed list (per goods-return record)
  const [details, setDetails] = useState([]);
  const [selectedRef, setSelectedRef] = useState(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (companyId) params.set('companyId', companyId);
      if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.set('dateTo', filters.dateTo);
      const res = await api.get(`/goods-returns/reports/by-reference?${params}`);
      setRows(res.data.data || []);
    } catch (err) {
      console.error('Failed to load report:', err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, filters]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const drillDown = async (referenceId) => {
    setSelectedRef(referenceId);
    setDetails([]);
    try {
      const params = new URLSearchParams({ limit: 100 });
      if (companyId) params.set('companyId', companyId);
      if (referenceId) params.set('referenceId', referenceId);
      if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.set('dateTo', filters.dateTo);
      const res = await api.get(`/goods-returns?${params}`);
      setDetails(res.data.data || []);
    } catch (err) {
      console.error('Failed drill-down:', err);
    }
  };

  const card = darkMode ? 'bg-gray-800 border-gray-700 text-gray-100' : 'bg-white border-gray-200 text-gray-900';
  const inputCls = `px-3 py-2 rounded border ${darkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300'}`;

  const totals = rows.reduce((acc, r) => {
    acc.returnsCount += r.returnsCount || 0;
    acc.totalReturnedAmount += r.totalReturnedAmount || 0;
    acc.totalTTReversed += r.totalTTReversed || 0;
    acc.totalCommissionCollected += r.totalCommissionCollected || 0;
    return acc;
  }, { returnsCount: 0, totalReturnedAmount: 0, totalTTReversed: 0, totalCommissionCollected: 0 });

  return (
    <div className={`p-4 md:p-6 min-h-full ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'}`}>
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><FaUndo /> Goods Return Reports</h1>
          <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
            Per-reference summary of returns: TT (commission) reversed and amounts collected back from references.
          </p>
        </div>
        <button onClick={fetchSummary} className={`inline-flex items-center gap-2 px-3 py-2 rounded border ${darkMode ? 'border-gray-600 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-50'}`}>
          <FaSync /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div className={`p-3 mb-4 rounded border flex flex-wrap items-end gap-3 ${card}`}>
        <div>
          <label className="block text-xs text-gray-500 mb-1">From</label>
          <input type="date" className={inputCls} value={filters.dateFrom}
            onChange={e => setFilters(f => ({ ...f, dateFrom: e.target.value }))} />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">To</label>
          <input type="date" className={inputCls} value={filters.dateTo}
            onChange={e => setFilters(f => ({ ...f, dateTo: e.target.value }))} />
        </div>
        <button onClick={fetchSummary} className="inline-flex items-center gap-2 px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700">
          <FaFilter /> Apply
        </button>
      </div>

      {/* Summary table */}
      <div className={`rounded border overflow-x-auto ${card}`}>
        <table className="w-full text-sm">
          <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-100'}>
            <tr>
              <th className="text-left px-3 py-2">Reference ID</th>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Phone</th>
              <th className="text-right px-3 py-2">Returns</th>
              <th className="text-right px-3 py-2">Returned ₹</th>
              <th className="text-right px-3 py-2">TT Reversed</th>
              <th className="text-right px-3 py-2">Collected</th>
              <th className="text-center px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-8">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-8 text-gray-500">No goods returns found in this period</td></tr>
            ) : rows.map(r => (
              <tr key={String(r.referenceId || 'none')} className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <td className="px-3 py-2 font-mono">{r.referenceCode || '—'}</td>
                <td className="px-3 py-2">{r.referenceName || <span className="italic text-gray-500">No reference</span>}</td>
                <td className="px-3 py-2">{r.referencePhone || '—'}</td>
                <td className="px-3 py-2 text-right">{r.returnsCount}</td>
                <td className="px-3 py-2 text-right">{inr(r.totalReturnedAmount)}</td>
                <td className="px-3 py-2 text-right text-orange-700">{inr(r.totalTTReversed)}</td>
                <td className="px-3 py-2 text-right text-green-700">{inr(r.totalCommissionCollected)}</td>
                <td className="px-3 py-2 text-center">
                  <button className="text-blue-600 underline text-xs" onClick={() => drillDown(r.referenceId)}>View Returns</button>
                </td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className={`border-t-2 font-semibold ${darkMode ? 'bg-gray-700/60 border-gray-600' : 'bg-gray-100 border-gray-300'}`}>
                <td colSpan={3} className="px-3 py-2 text-right">Totals</td>
                <td className="px-3 py-2 text-right">{totals.returnsCount}</td>
                <td className="px-3 py-2 text-right">{inr(totals.totalReturnedAmount)}</td>
                <td className="px-3 py-2 text-right text-orange-700">{inr(totals.totalTTReversed)}</td>
                <td className="px-3 py-2 text-right text-green-700">{inr(totals.totalCommissionCollected)}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Details */}
      {selectedRef !== null && (
        <div className={`mt-6 rounded border ${card}`}>
          <div className={`flex items-center justify-between p-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h2 className="font-semibold">Returns Detail</h2>
            <button className="text-sm text-gray-500 hover:underline" onClick={() => { setSelectedRef(null); setDetails([]); }}>Close</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-100'}>
                <tr>
                  <th className="text-left px-3 py-2">Return ID</th>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">DO Number</th>
                  <th className="text-right px-3 py-2">Returned ₹</th>
                  <th className="text-right px-3 py-2">TT Reversed</th>
                  <th className="text-right px-3 py-2">Collected</th>
                  <th className="text-left px-3 py-2">Created By</th>
                </tr>
              </thead>
              <tbody>
                {details.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-6 text-gray-500">No detail rows</td></tr>
                ) : details.map(r => (
                  <tr key={r._id} className={`border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <td className="px-3 py-2 font-mono">{r.returnId}</td>
                    <td className="px-3 py-2">{r.returnDate ? new Date(r.returnDate).toLocaleDateString() : ''}</td>
                    <td className="px-3 py-2">{r.doNumber}</td>
                    <td className="px-3 py-2 text-right">{inr(r.totalReturnedAmount)}</td>
                    <td className="px-3 py-2 text-right text-orange-700">{inr(r.totalTTReversed)}</td>
                    <td className="px-3 py-2 text-right text-green-700">{inr(r.commissionCollection?.amountCollected)}</td>
                    <td className="px-3 py-2">{r.createdBy?.name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoodsReturnReports;
